import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Product, Purchase, Subscription } from 'referral-types';
import useSWR from 'swr';
import { baseEndpoint as discountCacheKey } from '../../api/UserDiscountApi';
import { baseEndpoint as subscriptionCacheKey } from '../../api/SubscriptionApi';
import { baseEndpoint as productApiCacheKey } from '../../api/ProductApi';
import { productApi, refreshAuthSession, subscriptionApi, userDiscountApi } from '../../api/service';
import { useAuth } from '../auth/useAuth';
import { PurchaseContext } from './PurchaseContext';
import { UNLIMITED_REFERRAL_PRODUCT_ID } from './constants';
import toast from 'react-hot-toast';

const StripeKey = import.meta.env.VITE_STRIPE_KEY;

export const PurchaseProvider = (props: Props) => {
  const { children } = props;
  const { currentUser, setCurrentUser } = useAuth();
  const [cart, setCart] = useState<Array<Product>>([]);
  const [altCart, setAltCart] = useState<Array<Product>>([]);

  const [completedPurchase, setCompletedPurchase] = useState<Purchase>();
  const prevCompletedPurchaseRef = useRef<Purchase>();

  const [completedSubscription, setCompletedSubscription] = useState<Subscription>();
  const prevCompletedSubscriptionRef = useRef<Subscription>();

  const [isUnlimitedReferralCartToggled, setIsUnlimitedReferralCartToggled] = useState<boolean>();
  const [isCheckingOut, setIsCheckingOut] = useState<boolean>();
  const [isSubscribing, setIsSubscribing] = useState<boolean>();
  const [stripePromise, setStripePromise] = useState<Stripe | null>(null);
  const [isPurchasingProducts, setIsPurchasingProducts] = useState<boolean>();

  const [isRefreshingToken, setIsRefreshingToken] = useState<boolean>();

  useEffect(() => {
    const prevCompletedPurchase = prevCompletedPurchaseRef.current;

    if (prevCompletedPurchase?.id !== completedPurchase?.id) {
      setIsRefreshingToken(true);
      refreshAuthSession()
        .then((r) => setCurrentUser(r))
        .catch(() => toast.error('Token Update Failure... Please login again to apply purchase. Sorry!'))
        .finally(() => setIsRefreshingToken(false));
    }

    prevCompletedPurchaseRef.current = completedPurchase;
  }, [completedPurchase, setCurrentUser]);

  useEffect(() => {
    const prevCompletedSubscription = prevCompletedSubscriptionRef.current;

    if (prevCompletedSubscription?.id !== completedSubscription?.id) {
      setIsRefreshingToken(true);
      refreshAuthSession()
        .then((r) => setCurrentUser(r))
        .catch(() => toast.error('Token Update Failure... Please login again to apply purchase. Sorry!'))
        .finally(() => setIsRefreshingToken(false));
    }

    prevCompletedSubscriptionRef.current = completedSubscription;
  }, [completedSubscription, setCurrentUser]);

  const { data: userDiscounts, mutate: userDiscountMutator } = useSWR(
    currentUser ? [discountCacheKey, currentUser.id, currentUser.roles.join()] : undefined,
    () => userDiscountApi.getDiscounts(),
    { suspense: false }
  );

  const { data: userSubscriptions, mutate: userSubscriptionsMutator } = useSWR(
    currentUser ? [subscriptionCacheKey, currentUser.id] : undefined,
    () => subscriptionApi.getUserSubscriptions(currentUser!.id),
    { suspense: false }
  );

  const { data: unlimitedReferralProduct } = useSWR(
    currentUser != null && !currentUser.hasUnlimitedReferrals
      ? [productApiCacheKey, currentUser.id, UNLIMITED_REFERRAL_PRODUCT_ID]
      : undefined,
    () => productApi.getProductById(UNLIMITED_REFERRAL_PRODUCT_ID),
    { suspense: false }
  );

  const toggleUnlimtedReferralCart = useCallback(() => {
    if (isUnlimitedReferralCartToggled) {
      setCart(altCart);
      setAltCart([]);
      setIsUnlimitedReferralCartToggled(false);
    } else {
      setAltCart(cart);

      if (unlimitedReferralProduct != null) {
        setCart([unlimitedReferralProduct]);
      }

      setIsUnlimitedReferralCartToggled(true);
    }
  }, [altCart, cart, isUnlimitedReferralCartToggled, unlimitedReferralProduct]);

  const toggleProduct = useCallback((product: Product) => {
    setCart((currCart) =>
      currCart.map((o) => o.id).includes(product.id)
        ? currCart.filter((o) => o.id !== product.id)
        : [...currCart, product]
    );
  }, []);

  useEffect(() => {
    loadStripe(StripeKey!).then((stripe) => setStripePromise(stripe));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleSubscribing = () => {
    if (isCheckingOut && !isSubscribing) {
      setIsCheckingOut(false);
      setTimeout(() => setIsSubscribing(true), 500);
    } else {
      setIsSubscribing((isToggled) => !isToggled);
    }
  };

  return (
    <PurchaseContext.Provider
      value={{
        isSubscribing,
        toggleSubscribing,

        isCheckingOut,
        setIsCheckingOut,

        isRefreshingToken,

        completedPurchase,
        setCompletedPurchase,

        completedSubscription,
        setCompletedSubscription,

        stripePromise,

        cart,
        toggleProduct,
        clearCart,

        toggleUnlimtedReferralCart,
        isUnlimitedReferralCartToggled,
        unlimitedReferralProduct,

        userDiscounts,
        userDiscountMutator,

        userSubscriptions,
        userSubscriptionsMutator,

        isPurchasingProducts,
        setIsPurchasingProducts,
      }}
    >
      {children}
    </PurchaseContext.Provider>
  );
};

type Props = {
  isSubscribing?: boolean;
  toggleSubscribing?: () => void;
  children: ReactNode;
};
